import { matchRoutes, type Params, type RouteObject } from "react-router";

import type { RouteHandle } from "shell/platform/types/types";

export const DEFAULT_PRODUCT_AREA = "shell";

/** Every product area an event can be tagged with: module ids plus overrides. */
export const KNOWN_PRODUCT_AREAS = [
  DEFAULT_PRODUCT_AREA,
  "badger",
  "client-lifecycle",
  "common-services",
  "customer-account",
  "customer-simulation",
  "instrument-issuance",
  "ledger-engine-simulation",
  "login",
  "organization",
  "sponsor-guard",
  "storefront-simulation",
  "thredd-portal",
] as const;
export const UNMATCHED_PATH = "/*";

const ROUTES_TIMEOUT_MS = 3000;

let routeConfig: RouteObject[] | null = null;
let resolveRoutesReady: () => void = () => {};
const routesReady = new Promise<void>(resolve => {
  resolveRoutesReady = resolve;
});

/** Registers the router config so analytics can resolve routes from URLs. */
export const setRouteConfig = (routes: RouteObject[]) => {
  routeConfig = routes;
  resolveRoutesReady();
};

/**
 * Resolves once the route config is registered, or false after a timeout so
 * events are never held back (they are then redacted to the unmatched path).
 */
export const whenRoutesReady = async (
  timeoutMs = ROUTES_TIMEOUT_MS,
): Promise<boolean> => {
  if (routeConfig) return true;

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<boolean>(resolve => {
    timer = setTimeout(() => resolve(false), timeoutMs);
  });
  try {
    return await Promise.race([routesReady.then(() => true), timeout]);
  } finally {
    clearTimeout(timer);
  }
};

const safeDecode = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/**
 * Replaces every concrete route param value in the pathname with `:key`, so no
 * identifier survives. The splat param becomes a trailing `/*`.
 */
export const toSafePath = (pathname: string, params: Params<string>) => {
  const { "*": splat, ...named } = params;
  const entries = Object.entries(named).filter(
    (entry): entry is [string, string] => !!entry[1],
  );

  let prefix = pathname;
  const hasSplat = !!splat;
  if (hasSplat) {
    const index = prefix.lastIndexOf(splat);
    prefix = index === -1 ? prefix : prefix.slice(0, index);
  }

  const segments = prefix.split("/").map(segment => {
    const decoded = safeDecode(segment);
    const hit = entries.find(([, value]) => value === decoded);
    return hit ? `:${hit[0]}` : segment;
  });

  const safe = segments.join("/");
  return hasSplat ? `${safe.replace(/\/$/, "")}/*` : safe || "/";
};

export interface RouteInfo {
  area: string;
  path: string;
}

/** Resolves the safe path and product area for a pathname. */
export const getRouteInfo = (pathname: string): RouteInfo => {
  if (!routeConfig) {
    return { area: DEFAULT_PRODUCT_AREA, path: UNMATCHED_PATH };
  }

  const matches = matchRoutes(routeConfig, { pathname });
  if (!matches?.length) {
    return { area: DEFAULT_PRODUCT_AREA, path: UNMATCHED_PATH };
  }

  const deepest = matches.at(-1);
  const area =
    [...matches]
      .reverse()
      .map(
        match => (match.route.handle as RouteHandle | undefined)?.productArea,
      )
      .find(Boolean) ?? DEFAULT_PRODUCT_AREA;

  return { area, path: toSafePath(pathname, deepest?.params ?? {}) };
};
